import html2pdf from 'html2pdf.js';

class PdfExportService {
    /**
     * Export multiple slides to a single full-page PDF
     * @param {Array} slides - Array of slide objects with HTML content
     * @param {String} filename - PDF filename
     * @param {Object} options - Additional html2pdf options
     */
    exportSlidesToPdf(slides, filename = 'slides.pdf', options = {}) {
        if (!slides || slides.length === 0) {
            console.error('PDF Export: No slides to export');
            throw new Error('No slides to export');
        }

        console.log('PDF Export: Starting export of', slides.length, 'slides');
        console.log('PDF Export: Slide types:', slides.map(slide =>
            `${slide.title}: ${typeof slide.content} (${slide.content ? slide.content.substring(0, 30) + '...' : 'empty'})`
        ));

        // Create the container for all slides
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.top = '0'; // Changed from -9999px to ensure content is rendered
        container.style.left = '0';
        container.style.width = '297mm'; // Exact A4 landscape size
        container.style.height = 'auto';
        container.style.backgroundColor = 'white';
        container.style.zIndex = '-1000'; // Keep it behind other content
        container.setAttribute('id', 'pdf-export-container');

        // Log container creation
        console.log('PDF Export: Created container with dimensions:', {
            width: container.style.width,
            height: container.style.height
        });

        // Inject CSS for proper page breaks and print settings
        const style = document.createElement('style');
        style.textContent = `
            @page {
                size: 297mm 210mm;
                margin: 0;
            }
            @media print {
                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                }
            }
            .slide-page {
                width: 297mm;
                height: 210mm;
                page-break-after: always;
                background: white;
                margin: 0;
                padding: 0;
                overflow: hidden;
                position: relative;
                box-sizing: border-box;
            }
            .slide-page:last-child {
                page-break-after: avoid;
            }
            .slide-content {
                width: 100%;
                height: 100%;
                position: relative;
                box-sizing: border-box;
            }
        `;
        container.appendChild(style);

        // Debug info element
        const debugInfo = document.createElement('div');
        debugInfo.style.position = 'fixed';
        debugInfo.style.top = '10px';
        debugInfo.style.right = '10px';
        debugInfo.style.background = 'rgba(255,255,255,0.8)';
        debugInfo.style.padding = '10px';
        debugInfo.style.border = '1px solid #ccc';
        debugInfo.style.borderRadius = '5px';
        debugInfo.style.zIndex = '10000';
        debugInfo.style.fontSize = '12px';
        debugInfo.style.fontFamily = 'monospace';
        debugInfo.style.display = 'none'; // Initially hidden
        document.body.appendChild(debugInfo);

        // Toggle debug function
        const toggleDebug = () => {
            debugInfo.style.display = debugInfo.style.display === 'none' ? 'block' : 'none';
        };

        // Debug keypress listener
        document.addEventListener('keydown', (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                toggleDebug();
            }
        });
        // Append each slide's content to the container
        slides.forEach((slide, index) => {

            const slideWrapper = document.createElement('div');
            slideWrapper.className = 'slide-page';
            slideWrapper.id = `slide-${index}`;

            const slideContent = document.createElement('div');
            slideContent.className = 'slide-content';

            // Verify slide content is non-empty
            if (!slide.content || slide.content.trim() === '') {
                slideContent.innerHTML = `<h1 style="color:red">ERROR: Empty content for slide ${index + 1} - ${slide.title}</h1>`;
            } else {
                slideContent.innerHTML = slide.content;
            }

            slideWrapper.appendChild(slideContent);
            container.appendChild(slideWrapper);
        });

        // Append container to DOM
        document.body.appendChild(container);

        // Force layout calculation (important for rendering)
        container.getBoundingClientRect();

        // No slide number badges as per request
        // Force browser to recalculate layout for each slide
        slides.forEach((_, index) => {
            const slideElement = document.getElementById(`slide-${index}`);
            if (slideElement) {
                // Force layout recalculation for better rendering
                slideElement.getBoundingClientRect();
            }
        });

        // Set html2pdf options with improved settings
        const pdfOptions = {
            margin: 0,
            filename,
            image: { type: 'jpeg', quality: 1.0 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: true,
                allowTaint: true,
                letterRendering: true,
                scrollX: 0,
                scrollY: 0,
                windowWidth: document.documentElement.offsetWidth,
                windowHeight: document.documentElement.offsetHeight
            },
            jsPDF: {
                unit: 'mm',
                format: 'a4',
                orientation: 'landscape',
                compress: true,
                hotfixes: ["px_scaling"]
            },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
            ...options
        };

        return new Promise<void>((resolve, reject) => {
            // Add a longer delay to ensure DOM is fully rendered
            setTimeout(() => {
                // Function to capture images first, then create PDF
                const captureAndCreatePdf = async () => {
                    try {
                        const html2canvasModule = window['html2canvas'] || (await import('html2canvas')).default;
                        const jsPDFModule = window['jspdf'] || (await import('jspdf')).default;

                        // Create a new PDF document
                        const pdf = new jsPDFModule({
                            orientation: 'landscape',
                            unit: 'mm',
                            format: 'a4'
                        });

                        // Process each slide individually
                        for (let i = 0; i < slides.length; i++) {
                            const slideElement = document.getElementById(`slide-${i}`);
                            if (!slideElement) {
                                continue;
                            }

                            // Capture the slide
                            const canvas = await html2canvasModule(slideElement, {
                                scale: 2,
                                useCORS: true,
                                logging: true,
                                backgroundColor: '#ffffff',
                                allowTaint: true,
                                letterRendering: true
                            });

                            // Convert to image and add to PDF
                            const imgData = canvas.toDataURL('image/jpeg', 1.0);

                            // Add new page except for the first slide
                            if (i > 0) {
                                pdf.addPage();
                            }

                            // Add image to the PDF
                            pdf.addImage(imgData, 'JPEG', 0, 0, 297, 210);
                        }

                        // Save the PDF
                        pdf.save(filename);

                        // Clean up
                        document.body.removeChild(container);
                        resolve();
                    } catch (error) {
                        console.error('PDF Export: Failed during capture', error);
                        reject(error);
                    }
                };

                // Try manual approach first, fall back to html2pdf if it fails
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                captureAndCreatePdf().catch((error) => {

                    // Fallback to html2pdf
                    html2pdf()
                        .set(pdfOptions)
                        .from(container)
                        .save()
                        .then(() => {
                            document.body.removeChild(container);
                            resolve();
                        })
                        .catch((error) => {
                            console.error('PDF Export: Failed with error', error);
                            document.body.removeChild(container);
                            reject(error);
                        });
                });
            }, 1000); // Increased delay to 1000ms for better rendering
        });
    }

    /**
     * Check if the necessary libraries are available
     * @returns {Promise<boolean>} true if all libraries are available
     */
    async checkDependencies() {
        try {
            // Check for html2pdf
            if (typeof html2pdf !== 'function') {
                console.error('PDF Export: html2pdf.js is not available');
                return false;
            }

            console.log('PDF Export: All dependencies available');
            return true;
        } catch (error) {
            console.error('PDF Export: Dependency check failed', error);
            return false;
        }
    }
}

export const pdfExportService = new PdfExportService();
